/**
 * src/api/ismsWritersApi.js
 *
 * Thin client for the msr 4.1b server writers (GH #7351) — the only write
 * paths for an SoA version's approval, an obligation's enablement and the
 * risk method. Each stamps the server's time and the authenticated actor and
 * records the change in the isms-change ledger. state-write no longer takes
 * these fields from a whole-state save, so a local-only edit would never
 * persist: callers apply what the server returns.
 *
 * Each endpoint commits the tenant_state row, so the transport claims and
 * adopts the state version around it (src/api/tenantStateVersion.js).
 */

import { azurePost, getTenantId } from './azureClient';

/** A request key the server uses to recognise a retry of the same approval. */
export function newRequestKey() {
  const rnd =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `req-${rnd}`.slice(0, 100);
}

/**
 * Approve a pending SoA version (SOA-B-001, SOA-B-002).
 * Returns { ok, version, stateVersion?, ismsChangeId?, ismsChangeTriggerRefused? }.
 */
export async function approveSoaVersion({
  frameworkId,
  versionId,
  comment,
  requestKey,
  ismsChangeTrigger,
}) {
  return azurePost('/api/soa-approve', {
    tenantId: getTenantId(),
    frameworkId,
    versionId,
    comment: comment || null,
    requestKey,
    ...(ismsChangeTrigger ? { ismsChangeTrigger } : {}),
  });
}

/**
 * Enable one or more obligations. `markInScope` also records the in-scope
 * decision in the same write. Returns { ok, entries, enabled, decisions, stateVersion? }.
 */
export async function enableObligations(obligationIds, { assigneeId, markInScope = false } = {}) {
  return azurePost('/api/obligations-enable', {
    tenantId: getTenantId(),
    obligationIds,
    ...(assigneeId !== undefined ? { assigneeId } : {}),
    markInScope,
  });
}

/**
 * Save the risk method: `riskSettings` and/or `criticalityScale`.
 * `expectedUpdatedAt` names, for every key sent, the `updatedAt` of that key
 * as the screen loaded it (null when it carried none). The server refuses a
 * save whose base is no longer the stored one with 409 'stale-version'
 * (GH #6524) — see isStaleRiskMethod().
 * Returns { ok, riskSettings?, criticalityScale?, changed, unchanged, stateVersion? }.
 */
export async function saveRiskMethod(method, expectedUpdatedAt) {
  return azurePost('/api/risk-method-write', {
    tenantId: getTenantId(),
    ...method,
    expectedUpdatedAt,
  });
}

/** The `updatedAt` a risk-method key was loaded with — the base of its next save. */
export function riskMethodBase(value) {
  return value && typeof value.updatedAt === 'string' && value.updatedAt ? value.updatedAt : null;
}

/** True when a saveRiskMethod() rejection means someone else saved first. */
export function isStaleRiskMethod(err) {
  return err?.httpStatus === 409 && err?.conflictReason === 'stale-version';
}
